// Döviz kuru widget'ı — Blazor ExchangeRateWidgetCard (`api/system/ExchangeRates/Latest`).
// Yanıt tek bir kur kaydıdır; birimler `currencyUnits` altındadır.
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/shared/api/http/client';
import { ENDPOINTS } from '@/shared/api/endpoints';
import { formatNumber } from '@/shared/lib/octo/formatters';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Spinner } from '@/shared/ui/Spinner';

interface CurrencyUnit {
  exchangeRateCurrencyUnitId: number;
  currencyCode: string | null;
  currencyName: string | null;
  currencyBuyRate: number | null;
  currencySellRate: number | null;
}
interface ExchangeRateDto {
  exchangeRateDate?: string | null;
  currencyUnits?: CurrencyUnit[] | null;
}

export function ExchangeRateWidget() {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ['home', 'exchangeRates'],
    queryFn: () => apiClient.get<ExchangeRateDto | ExchangeRateDto[]>(ENDPOINTS.EXCHANGE_RATES_LATEST, { octo: { expectSuccess: false } }),
    staleTime: 15 * 60_000,
  });
  if (q.isLoading)
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  const record = Array.isArray(q.data) ? q.data[0] : q.data;
  const rows = (record?.currencyUnits ?? []).filter((u) => u.currencyCode);
  if (!rows.length) return <EmptyState title={t('home.noRates')} />;
  return (
    <div className="flex h-full flex-col">
      {record?.exchangeRateDate ? <div className="text-fg-subtle mb-1 text-xs">{new Date(record.exchangeRateDate).toLocaleDateString('tr-TR')}</div> : null}
      <div className="min-h-0 flex-1 overflow-auto">
        <table className="w-full text-sm">
          <thead className="text-fg-subtle bg-bg-elevated sticky top-0 text-left text-xs">
            <tr>
              <th className="py-0.5">{t('home.currency')}</th>
              <th className="py-0.5 text-right">{t('home.buying')}</th>
              <th className="py-0.5 text-right">{t('home.selling')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.exchangeRateCurrencyUnitId} className="border-border border-t">
                <td className="py-1 font-medium" title={r.currencyName ?? undefined}>
                  {r.currencyCode}
                </td>
                <td className="py-1 text-right tabular-nums">{formatNumber(r.currencyBuyRate, 4)}</td>
                <td className="py-1 text-right tabular-nums">{formatNumber(r.currencySellRate, 4)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
